
import { CreateElement } from 'vue'
import { Component, Vue, Prop, Emit } from 'vue-property-decorator'
import {
  addMenuRedesignIconId,
  getMobileMenuAccountIcon,
  MainMenu,
  MainMenuGaClick,
} from '@mg/micro/vue2'
import type { MenuTreeItem } from '@mg/micro/vue2'
import { MobileMainMenu } from '@mg/micro/vue2/MobileMainMenu'
import { getMenuGQL, MENU_TYPE } from '@mg/micro/data/gql/menu'
import {
  GQLContent,
  GQLMenu,
  GQLUser,
} from '~/graphql/__generated__/interfaces'
import {
  GET_LOCAL_MENU_VISIBILITY,
  SET_HEADER_VISIBILITY,
} from '~/graphql/gqlLocal/localHeader'
import {
  GQLLocalAccount,
  GQLLocalHeader,
} from '~/graphql/__generated__/interfacesLocal'
import ROUTES from '~/config/routes'
import { GET_BASIC_USER_INFO } from '~/graphql/gql/user'
import {
  GET_CONTENT_BY_TITLE,
  GQLGetContentByTitleQueryInput,
} from '~/graphql/gql/content'
import { GQL_LOCAL_ACCOUNT } from '~/graphql/gqlLocal/localAccount'
import { GtmEventTypes } from '~/utils/tracking/gtm'
import { createUrl } from '~/utils/routes'
import { MainMenuTranslated } from '~/components/account/types'
import { getMenuVariables } from '~/utils/getMenuVariables'
import ApolloFetchPolicies from '~/utils/apolloFetchPolicies'

// vue2-teleport references `HTMLElement` while being imported, which throws on the server
// (SSR), so it is loaded in the browser only. The mobile menu it wraps is opened by user
// interaction, so nothing is rendered through it on the server anyway.
const Teleport = () =>
  process.client
    ? import('vue2-teleport')
    : Promise.resolve({ render: (h: CreateElement) => h() })

@Component<HeaderMainMenu>({
  apollo: {
    account: GQL_LOCAL_ACCOUNT,
    getContentByTitle: {
      fetchPolicy: ApolloFetchPolicies.CACHE_ONLY,
      query: GET_CONTENT_BY_TITLE,
      skip(): boolean {
        const menuIsVisible = this.header?.menuIsVisible || false
        return !menuIsVisible
      },
      variables: {
        title: 'web_header_horizon_menu',
      } as GQLGetContentByTitleQueryInput,
    },
    getMenu: {
      query: getMenuGQL,
      variables() {
        return getMenuVariables(
          MENU_TYPE.WEB,
          this.$route.query.naviPreview as string,
          this.$shopConfig.abTests.abTestsData.menuAbTest,
          this.header?.menuIsVisible || this.isHomepage || this.prefetchFullMenu
        )
      },
    },
    getUserMenu: {
      fetchPolicy: ApolloFetchPolicies.CACHE_ONLY,
      query: getMenuGQL,
      skip() {
        return !this.$shopConfig.user?.id && !this.account?.isLogged
      },
      update(data: { getMenu: GQLMenu }) {
        return data.getMenu
      },
      variables() {
        return getMenuVariables(
          MENU_TYPE.KC,
          this.$route.query.naviPreview as string,
          this.$shopConfig.abTests.abTestsData.menuAbTest,
          false,
        )
      },
    },
    getHorizonMenu: {
      fetchPolicy: ApolloFetchPolicies.CACHE_ONLY,
      query: getMenuGQL,
      update(data: { getMenu: GQLMenu }) {
        return data.getMenu
      },
      variables() {
        return getMenuVariables(
          MENU_TYPE.HORIZON,
          this.$route.query.naviPreview as string,
          this.$shopConfig.abTests.abTestsData.menuAbTest,
          false,
        )
      },
    },
    header: {
      query: GET_LOCAL_MENU_VISIBILITY,
    },
    user: {
      query: GET_BASIC_USER_INFO,
      skip() {
        return !this.$shopConfig.user?.id && !this.account?.isLogged
      },
    },
  },
  components: { MainMenu, Teleport, MobileMainMenu },
})
export default class HeaderMainMenu extends Vue {
  @Prop(String) shopId: string
  @Prop(Boolean) isHomepage: boolean
  @Prop(Boolean) isWinterEdition: boolean
  @Prop(Boolean) isCatalogOpen: boolean
  ABOVE_HEADER_ELEMENT_ID = 'above-header'
  PREFETCH_FULL_MENU_DELAY = 1000
  getMenu: GQLMenu
  getUserMenu: GQLMenu
  getHorizonMenu: GQLMenu
  account: GQLLocalAccount
  header: GQLLocalHeader
  user!: GQLUser
  getContentByTitle: GQLContent
  prefetchFullMenu = false

  mounted() {
    if (process.client) {
      document.addEventListener('readystatechange', this.prefetchMenuListener)
    }
  }

  prefetchMenuListener(event: Event) {
    const target = event.target as Document

    if (target.readyState === 'complete') {
      setTimeout(() => {
        this.prefetchFullMenu = true
      }, this.PREFETCH_FULL_MENU_DELAY)
    }
  }

  setMenuVisibility(isVisible: boolean) {
    this.$apollo.mutate({
      mutation: SET_HEADER_VISIBILITY,
      variables: {
        isMenuVisible: isVisible,
      },
    })
  }

  @Emit('closed')
  hideMobileMenu() {
    this.gaEventClick({
      eventAction: 'Mobile navigation',
      eventLabel: 'Close',
      eventFlag: true,
    })
  }

  closeMobileMainMenu() {
    this.setMenuVisibility(false)
    this.gaEventClick({
      eventAction: 'Mobile navigation',
      eventLabel: 'Close',
      eventFlag: true,
    })
  }

  async logout(event: Event) {
    event.preventDefault()

    try {
      await this.$webApi.logout()
    } catch (e) {
      this.$logger.error(`HeaderMainMenu.logout - logout error : ${e}`, e)
    }

    window.location.replace('/')
  }

  get menuItems() {
    const menuData = (this.getMenu?.data ?? []) as unknown as MenuTreeItem[]

    return addMenuRedesignIconId(
      menuData as unknown as MenuTreeItem[],
      this.$shopConfig.webConfig.menuIconsRedesign,
    )
  }

  get translations(): MainMenuTranslated {
    return {
      all_categories: this.$t('common.cta_category_select'),
      other_categories: this.$t('common.cta_other_categories'),
      full_offer: this.$t('common.full_offer'),
      hdg_account: this.$t('common.hdg_account'),
      logout: this.$t('common.logout'),
      shopping: this.$t('common.shopping'),
      product_catalog: this.$t('common.product_catalog'),
    }
  }

  get shortName() {
    const { firstName, lastName } = this.user ?? { firstName: '', lastName: '' }
    const lastNameWithDot = lastName ? `${lastName.slice(0, 1)}.` : ''
    return `<strong>${firstName} ${lastNameWithDot}</strong>`
  }

  get mobileUserMenuData() {
    const isLogged = !!this.user
    const { shopId } = this.$shopConfig

    const { baseUrl } = this.$shopConfig.webConfig

    if (!isLogged) {
      return [
        {
          url: createUrl(ROUTES.clientMyAccount, shopId),
          name: this.$t('common.login'),
          image: getMobileMenuAccountIcon('login', baseUrl),
        },
      ]
    } else {
      return this.getUserMenu
        ? [
            ...this.getUserMenu.data,
            {
              image: getMobileMenuAccountIcon('logout', baseUrl),
              name: this.translations?.logout,
              url: '',
              isLogItem: true,
            },
          ]
        : []
    }
  }

  get mobileMenuTopHeight() {
    if (this.header.menuIsVisible) {
      const headerHeight =
        document
          .getElementById(this.ABOVE_HEADER_ELEMENT_ID)
          ?.getBoundingClientRect().height ?? 0

      const visiblePartAboveHeaderElement =
        document
          .getElementById(this.ABOVE_HEADER_ELEMENT_ID)
          ?.getBoundingClientRect().bottom ?? 0

      const top = window.pageYOffset || document.documentElement.scrollTop
      const consentHeight =
        visiblePartAboveHeaderElement > 0 ? visiblePartAboveHeaderElement : 0
      const calcHeight = top === 0 ? headerHeight : consentHeight
      const mobilMenuHeader = this.$resize.isTablet ? 72 : 58

      return mobilMenuHeader + calcHeight
    }
  }

  gaEventClick(event: MainMenuGaClick) {
    this.$pushAnalyticsEvent(GtmEventTypes.EventFire, {
      eventAction: event.eventAction,
      eventCategory: 'Navigation',
      eventLabel: event.eventLabel,
      eventNonInteractionFlag: event.eventFlag ?? false,
      eventValue: 1,
    })
  }

  get desktopPermanentVisibility() {
    return this.isHomepage && !this.header.menuDesktopHiddenList
  }

  beforeDestroy() {
    if (process.client) {
      window.removeEventListener('readystatechange', this.prefetchMenuListener)
    }
  }
}
